import { useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Upload, CheckCircle, XCircle, Loader2, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { API_URL } from "../config/api";


export default function DriverPodUploadPage() {
  const { orderId, token } = useParams();
  const [status, setStatus] = useState("idle"); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setErrorMessage("Please select an image or PDF file");
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage("File too large. Maximum 10MB.");
        return;
      }
      setSelectedFile(file);
      setErrorMessage("");
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setStatus("uploading");
    setErrorMessage("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch(
        `${API_URL}/pod/${orderId}/${token}/upload`,
        {
          method: "POST",
          body: formData
        }
      );

      if (response.ok) {
        setStatus("success");
      } else {
        const data = await response.json();
        setStatus("error");
        setErrorMessage(data.detail || "Upload failed");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage("Network error. Please try again.");
    }
  };

  const handleCameraCapture = () => {
    document.getElementById('camera-input').click();
  };

  const handleFileUpload = () => {
    document.getElementById('file-input').click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-900">Europacific</h1>
          <p className="text-gray-500 text-sm">POD / CMR Upload</p>
        </div>

        {/* Hidden file inputs */}
        <input
          id="camera-input"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
        <input
          id="file-input"
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {status === "idle" && !selectedFile && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Upload POD / CMR</h2>
              <p className="text-gray-600 text-sm">Take a photo or upload document</p>
            </div>

            <Button
              onClick={handleCameraCapture}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            >
              <Camera className="w-5 h-5 mr-2" />
              Take Photo
            </Button>

            <Button
              onClick={handleFileUpload}
              size="lg"
              variant="outline"
              className="w-full text-lg py-6"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload File
            </Button>
          </div>
        )}

        {status === "idle" && selectedFile && (
          <div className="space-y-4">
            {previewUrl && (
              <div className="border rounded-lg overflow-hidden">
                <img src={previewUrl} alt="Preview" className="w-full max-h-64 object-contain" />
              </div>
            )}
            
            {!previewUrl && selectedFile && (
              <div className="border rounded-lg p-4 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">{selectedFile.name}</p>
                <p className="text-xs text-gray-400">{(selectedFile.size / 1024).toFixed(1)} KB</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                variant="outline"
                className="flex-1"
              >
                Change
              </Button>
              <Button
                onClick={handleUpload}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>
        )}

        {status === "uploading" && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Uploading...</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-green-700 mb-2">Upload Complete!</h2>
            <p className="text-gray-600">Thank you! POD/CMR received.</p>
            <p className="text-gray-500 text-sm mt-4">You can close this page.</p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <Button
              onClick={() => { setStatus("idle"); setSelectedFile(null); setPreviewUrl(null); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t text-center text-xs text-gray-400">
          <p>Europacific Logistika d.o.o.</p>
          <p>+386 5 662 6380</p>
        </div>
      </motion.div>
    </div>
  );
}
